import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Table, Button, message, Space, Switch, Tag, Alert, Modal, Tour, ConfigProvider, Select } from 'antd';
import type { TourProps } from 'antd';
import type { DragEndEvent } from '@dnd-kit/core';
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { MenuOutlined, ReloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { homeService } from '../services/homeService';
import { coffeeService } from '../services/coffeeService';
import { bottledDrinkService } from '../services/bottledDrinkService';
import { collectionService } from '../services/collectionService';
import { DishService } from '../services/dishService';
import { storeService } from '../services/storeService';
import { storeMenuService } from '../services/storeMenuService';
import { HomeItem } from '../types/home';
import { CoffeeCollection } from '../types/collection';
import { CoffeeBean } from '../types/coffee';
import { BottledDrink } from '../types/bottledDrink';
import { Dish } from '../types/dish';
import { Store } from '../types/store';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';

type HomeItemType = 'coffee' | 'drink' | 'collection' | 'dish';
type ProductSourceType = 'coffee' | 'bottledDrink' | 'dish';
type ProductData = CoffeeBean | BottledDrink | Dish;

type ProductWithType = ProductData & {
    id: string;
    type: ProductSourceType;
};

const DraggableRow = ({ children, ...props }: any) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: props['data-row-key']
    });

    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999, background: '#fafafa' } : {}),
        cursor: 'move',
        touchAction: 'none'
    };

    return (
        <tr
            {...props}
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={`${props.className || ''} drag-row ${isDragging ? 'dragging-row' : ''}`}
        >
            {children}
        </tr>
    );
};

const HomeManagement: React.FC = () => {
    const [items, setItems] = useState<HomeItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isMobile = windowWidth <= 400;
    const dishServiceRef = useRef(new DishService());
    const isMountedRef = useRef(true);
    const fetchSequenceRef = useRef(0);

    const homeItemsCacheRef = useRef<HomeItem[] | null>(null);
    const collectionsCacheRef = useRef<CoffeeCollection[] | null>(null);
    const productsMapCacheRef = useRef<Map<string, ProductWithType> | null>(null);
    const storeMenuItemsCacheRef = useRef<Map<string, ProductWithType[]>>(new Map());

    // Store selection state
    const [stores, setStores] = useState<Store[]>([]);
    const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);

    // Tour state and refs
    const [isTourOpen, setIsTourOpen] = useState(false);
    const titleRef = useRef<HTMLHeadingElement | null>(null);
    const resetButtonRef = useRef<HTMLButtonElement | null>(null);
    const instructionsRef = useRef<HTMLDivElement | null>(null);
    const tableContainerRef = useRef<HTMLDivElement | null>(null);

    const getTourTarget = useCallback(<T extends HTMLElement>(ref: React.RefObject<T | null>) => {
        return () => ref.current ?? document.body;
    }, []);

    const tourSteps: TourProps['steps'] = [
        {
            title: 'Quản lý trang chủ',
            description: 'Chào mừng bạn đến với trang Quản lý trang chủ! Đây là nơi bạn có thể sắp xếp thứ tự hiển thị các sản phẩm trên trang chủ của ứng dụng theo từng cửa hàng. Tuyển tập sẽ chung cho tất cả cửa hàng, còn cà phê, đồ uống và món ăn sẽ dựa vào menu của từng cửa hàng.',
            target: getTourTarget(titleRef),
        },
        {
            title: 'Chọn cửa hàng',
            description: 'Trước tiên bạn cần chọn cửa hàng để quản lý menu trang chủ. Mỗi cửa hàng sẽ có các món khác nhau dựa trên menu đã được thiết lập.',
            target: getTourTarget(resetButtonRef),
        },
        {
            title: 'Làm mới danh sách',
            description: 'Nhấn vào đây để làm mới toàn bộ danh sách của cửa hàng đã chọn. Chức năng này sẽ đặt lại thứ tự sắp xếp về mặc định và bật hiển thị cho tất cả sản phẩm.',
            target: getTourTarget(resetButtonRef),
        },
        {
            title: 'Hướng dẫn sử dụng',
            description: 'Đọc hướng dẫn này để hiểu cách kéo thả sắp xếp thứ tự, bật/tắt hiển thị sản phẩm theo từng cửa hàng.',
            target: getTourTarget(instructionsRef),
        },
        {
            title: 'Bảng quản lý sản phẩm',
            description: 'Bảng này hiển thị sản phẩm của cửa hàng đã chọn. Bạn có thể kéo thả để sắp xếp thứ tự, bật/tắt hiển thị bằng switch. Tuyển tập luôn hiển thị, các món khác dựa vào menu cửa hàng.',
            target: getTourTarget(tableContainerRef),
        },
        {
            title: 'Kết thúc hướng dẫn!',
            description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn đã sẵn sàng quản lý giao diện trang chủ theo từng cửa hàng. Hãy chọn cửa hàng và sắp xếp menu phù hợp!',
            target: getTourTarget(titleRef),
        },
    ];

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1,
            },
        })
    );

    const getHomeItems = useCallback(async (forceReload = false): Promise<HomeItem[]> => {
        if (!forceReload && homeItemsCacheRef.current) {
            return homeItemsCacheRef.current;
        }

        const homeItems = await homeService.getAllHomeItems();
        homeItemsCacheRef.current = homeItems;
        return homeItems;
    }, []);

    const getCollections = useCallback(async (forceReload = false): Promise<CoffeeCollection[]> => {
        if (!forceReload && collectionsCacheRef.current) {
            return collectionsCacheRef.current;
        }

        const collections = await collectionService.getAllCollections();
        collectionsCacheRef.current = collections;
        return collections;
    }, []);

    const getProductsMap = useCallback(async (forceReload = false): Promise<Map<string, ProductWithType>> => {
        if (!forceReload && productsMapCacheRef.current) {
            return productsMapCacheRef.current;
        }

        const [coffees, drinks, dishes] = await Promise.all([
            coffeeService.getAllCoffees(),
            bottledDrinkService.getAllBottledDrinks(),
            dishServiceRef.current.getAllDishes()
        ]);

        const allProductsMap = new Map<string, ProductWithType>();

        coffees.forEach(coffee => {
            if (coffee.id) {
                allProductsMap.set(coffee.id, { ...coffee, id: coffee.id, type: 'coffee' });
            }
        });

        drinks.forEach(drink => {
            if (drink.id) {
                allProductsMap.set(drink.id, { ...drink, id: drink.id, type: 'bottledDrink' });
            }
        });

        dishes
            .filter(dish => dish.imageUrl)
            .forEach(dish => {
                if (dish.id) {
                    allProductsMap.set(dish.id, { ...dish, id: dish.id, type: 'dish' });
                }
            });

        productsMapCacheRef.current = allProductsMap;
        return allProductsMap;
    }, []);

    const getMenuItemsByStore = useCallback(async (
        storeId: string,
        forceMenuReload = false,
        forceProductsReload = false
    ): Promise<ProductWithType[]> => {
        if (!forceMenuReload) {
            const cachedMenuItems = storeMenuItemsCacheRef.current.get(storeId);
            if (cachedMenuItems) {
                return cachedMenuItems;
            }
        }

        const [storeMenus, allProductsMap] = await Promise.all([
            storeMenuService.getActiveMenusByStoreId(storeId),
            getProductsMap(forceProductsReload)
        ]);

        const menuProducts = new Map<string, ProductWithType>();
        storeMenus.forEach(menu => {
            menu.items?.forEach(item => {
                const productId = item.productId;
                if (!menuProducts.has(productId) && allProductsMap.has(productId)) {
                    const fullProduct = allProductsMap.get(productId);
                    if (fullProduct) {
                        menuProducts.set(productId, {
                            ...fullProduct,
                            id: productId
                        });
                    }
                }
            });
        });

        const menuItems = Array.from(menuProducts.values());
        storeMenuItemsCacheRef.current.set(storeId, menuItems);
        return menuItems;
    }, [getProductsMap]);

    const fetchStores = useCallback(async () => {
        try {
            const storesList = await storeService.getActiveStores();

            if (!isMountedRef.current) {
                return;
            }

            setStores(storesList);

            // Auto select first store if none selected
            if (storesList.length > 0) {
                setSelectedStoreId(prev => prev ?? storesList[0].id!);
            }
        } catch (error) {
            console.error('Error fetching stores:', error);
            message.error('Không thể tải danh sách cửa hàng');
        }
    }, []);

    const fetchAllItems = useCallback(async (options?: {
        forceHomeItemsReload?: boolean;
        forceCollectionsReload?: boolean;
        forceStoreMenuReload?: boolean;
        forceProductsReload?: boolean;
    }) => {
        if (!selectedStoreId) return;

        const currentFetchId = ++fetchSequenceRef.current;
        setLoading(true);

        try {
            const [existingHomeItems, collections, menuItems] = await Promise.all([
                getHomeItems(Boolean(options?.forceHomeItemsReload)),
                getCollections(Boolean(options?.forceCollectionsReload)),
                getMenuItemsByStore(
                    selectedStoreId,
                    Boolean(options?.forceStoreMenuReload),
                    Boolean(options?.forceProductsReload)
                )
            ]);

            if (!isMountedRef.current || currentFetchId !== fetchSequenceRef.current) {
                return;
            }

            const existingItemsMap = new Map(
                existingHomeItems.map(item => [item.itemId, item])
            );

            const allItems: HomeItem[] = [];
            let currentOrder = 0;

            // Process existing home items
            existingHomeItems.forEach(item => {
                let sourceItem: HomeItem['item'] | undefined;

                if (item.type === 'collection') {
                    sourceItem = collections.find(c => c.id === item.itemId);
                } else {
                    // For menu items (coffee, drink, dish)
                    sourceItem = menuItems.find(m => m.id === item.itemId);
                }

                if (sourceItem) {
                    allItems.push({
                        ...item,
                        item: sourceItem,
                        createdAt: item.createdAt,
                        updatedAt: item.updatedAt
                    });
                    currentOrder = Math.max(currentOrder, item.order);
                }
            });

            const addNewItems = async (itemsToAdd: Array<HomeItem['item'] & { id?: string }>, type: HomeItemType) => {
                for (const item of itemsToAdd) {
                    if (item.id && !existingItemsMap.has(item.id)) {
                        currentOrder++;
                        const newHomeItem: Omit<HomeItem, 'id' | 'createdAt' | 'updatedAt'> = {
                            type,
                            item,
                            isVisible: false,
                            order: currentOrder,
                            itemId: item.id
                        };
                        try {
                            const addedItem: any = await homeService.addHomeItem(newHomeItem);
                            allItems.push({
                                ...addedItem,
                                createdAt: new Date(),
                                updatedAt: new Date()
                            });
                            existingItemsMap.set(item.id, addedItem);
                        } catch (error) {
                            console.error(`Error adding new ${type}:`, error);
                        }
                    }
                }
            };

            // Add collections (common to all stores)
            await addNewItems(collections, 'collection');
            
            // Add menu items based on store selection - convert bottledDrink type to drink for consistency
            const coffeeItems = menuItems.filter(item => item.type === 'coffee').map(item => ({
                ...item,
                type: 'coffee'
            }));
            const drinkItems = menuItems.filter(item => item.type === 'bottledDrink').map(item => ({
                ...item,
                type: 'drink' // Convert bottledDrink to drink for UI consistency
            }));
            const dishItems = menuItems.filter(item => item.type === 'dish').map(item => ({
                ...item,
                type: 'dish'
            }));

            await addNewItems(coffeeItems, 'coffee');
            await addNewItems(drinkItems, 'drink');
            await addNewItems(dishItems, 'dish');

            const sortedItems = allItems.sort((a, b) => a.order - b.order);

            if (!isMountedRef.current || currentFetchId !== fetchSequenceRef.current) {
                return;
            }

            setItems(sortedItems);
            homeItemsCacheRef.current = sortedItems;

        } catch (error) {
            console.error('Error fetching items:', error);
            message.error('Không thể tải dữ liệu');
        } finally {
            if (isMountedRef.current && currentFetchId === fetchSequenceRef.current) {
                setLoading(false);
            }
        }
    }, [getCollections, getHomeItems, getMenuItemsByStore, selectedStoreId]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchStores();

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchStores]);

    useEffect(() => {
        if (!selectedStoreId) return;
        fetchAllItems();
    }, [fetchAllItems, selectedStoreId]);

    const handleDragEnd = useCallback(async ({ active, over }: DragEndEvent) => {
        if (active.id !== over?.id) {
            const oldIndex = items.findIndex(i => i.id === active.id);
            const newIndex = items.findIndex(i => i.id === over?.id);

            const newItems = arrayMove(items, oldIndex, newIndex).map(item => ({
                ...item,
                createdAt: item.createdAt || new Date(),
                updatedAt: new Date()
            }));
            setItems(newItems);

            try {
                await homeService.updateItemsOrder(newItems);
                message.success('Cập nhật thứ tự thành công');

                // Gọi api gửi dữ liệu đồ uống để AI tự động cập nhật (tách riêng, không ảnh hưởng đến UI)
                // try {
                //     await axios.get(`https://coffee-ai.8am.vn/update`);
                // } catch (aiError) {
                //     console.warn('AI update failed:', aiError);
                //     // Không hiển thị lỗi cho user vì đây là tính năng phụ
                // }
            } catch (error) {
                message.error('Không thể cập nhật thứ tự');
                setItems(items);
            }
        }
    }, [items]);

    const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});

    const handleVisibilityChange = useCallback(async (id: string, isVisible: boolean) => {
        try {
            setLoadingStates(prev => ({ ...prev, [id]: true }));

            await homeService.updateHomeItem(id, { isVisible });
            setItems(prevItems => prevItems.map(item =>
                item.id === id ? { ...item, isVisible } : item
            ));
            message.success('Cập nhật trạng thái thành công');

            // Set loading state thành false ngay sau khi cập nhật thành công
            setLoadingStates(prev => ({ ...prev, [id]: false }));

            // Gọi api gửi dữ liệu đồ uống để AI tự động cập nhật (tách riêng, không ảnh hưởng đến UI)
            // try {
            //     await axios.get(`https://coffee-ai.8am.vn/update`);
            // } catch (aiError) {
            //     console.warn('AI update failed:', aiError);
            //     // Không hiển thị lỗi cho user vì đây là tính năng phụ
            // }
        } catch (error) {
            message.error('Không thể cập nhật trạng thái');
            setLoadingStates(prev => ({ ...prev, [id]: false }));
        }
    }, []);

    const getImageUrl = useCallback((record: HomeItem) => {
        if (record.type === 'collection') {
            return (record.item as CoffeeCollection).imageUrl;
        } else if (record.type === 'dish') {
            return (record.item as Dish).imageUrl;
        } else {
            return (record.item as (CoffeeBean | BottledDrink)).images?.[0];
        }
    }, []);

    const getItemTypeName = useCallback((type: string) => {
        switch (type) {
            case 'coffee': return 'Hạt cà phê';
            case 'drink': return 'Đồ uống đóng chai';
            case 'collection': return 'Tuyển tập';
            case 'dish': return 'Đồ uống';
            default: return type;
        }
    }, []);

    const getItemTypeColor = useCallback((type: string) => {
        switch (type) {
            case 'coffee': return 'blue';
            case 'drink': return 'green';
            case 'collection': return 'purple';
            case 'dish': return 'orange';
            default: return 'default';
        }
    }, []);

    const columns = useMemo(() => [
        {
            title: '',
            dataIndex: 'sort',
            width: 30,
            className: 'drag-visible',
            render: () => <MenuOutlined style={{ cursor: 'move', color: '#999' }} />,
        },
        {
            title: 'Hình ảnh',
            dataIndex: ['item', 'imageUrl'],
            key: 'imageUrl',
            render: (url: string, record: HomeItem) => {
                const imageUrl = getImageUrl(record);
                return imageUrl ? (
                    <img
                        src={imageUrl}
                        alt="item"
                        style={{ width: 50, height: 50, objectFit: 'cover' }}
                    />
                ) : null;
            }
        },
        {
            title: 'Tên',
            dataIndex: ['item', 'name'],
            key: 'name',
        },
        {
            title: 'Loại',
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => {
                const color = getItemTypeColor(type);
                const text = getItemTypeName(type);
                return <Tag color={color}>{text}</Tag>;
            }
        },
        {
            title: 'Hiển thị',
            dataIndex: 'isVisible',
            key: 'isVisible',
            render: (isVisible: boolean, record: HomeItem) => (
                <Switch
                    checked={isVisible}
                    onChange={(checked) => handleVisibilityChange(record.id, checked)}
                    loading={loadingStates[record.id]}
                    disabled={loadingStates[record.id]}
                />
            )
        }
    ], [getImageUrl, getItemTypeColor, getItemTypeName, handleVisibilityChange, loadingStates]);

    const mobileColumns = useMemo(() => [
        {
            title: 'Sort',
            dataIndex: 'sort',
            width: 30,
            className: 'drag-visible',
            render: () => <MenuOutlined style={{ cursor: 'move', color: '#999' }} />,
        },
        {
            title: 'Thông tin',
            dataIndex: 'info',
            render: (_: any, record: HomeItem) => {
                const imageUrl = getImageUrl(record);

                return (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {imageUrl && (
                            <img
                                src={imageUrl}
                                alt="item"
                                style={{ width: 40, height: 40, objectFit: 'cover' }}
                            />
                        )}
                        <div style={{ flex: 1 }}>
                            <div>{record.item.name}</div>
                            <Space size={4}>
                                <Tag color={getItemTypeColor(record.type)}>
                                    {getItemTypeName(record.type)}
                                </Tag>
                                <Switch
                                    checked={record.isVisible}
                                    onChange={(checked) => handleVisibilityChange(record.id, checked)}
                                    size="small"
                                    loading={loadingStates[record.id]}
                                    disabled={loadingStates[record.id]}
                                />
                            </Space>
                        </div>
                    </div>
                );
            }
        }
    ], [getImageUrl, getItemTypeColor, getItemTypeName, handleVisibilityChange, loadingStates]);

    const handleResetItems = useCallback(async () => {
        try {
            setLoading(true);

            // Xóa tất cả items hiện có
            const currentItems = await getHomeItems(true);
            await Promise.all(currentItems.map(item => homeService.deleteHomeItem(item.id)));

            homeItemsCacheRef.current = null;

            // Fetch và tạo items mới
            await fetchAllItems({
                forceHomeItemsReload: true,
                forceStoreMenuReload: true
            });
            message.success('Đã reset danh sách thành công');

            // Gọi api gửi dữ liệu đồ uống để AI tự động cập nhật (tách riêng, không ảnh hưởng đến UI)
            // try {
            //     await axios.get(`https://coffee-ai.8am.vn/update`);
            // } catch (aiError) {
            //     console.warn('AI update failed:', aiError);
            //     // Không hiển thị lỗi cho user vì đây là tính năng phụ
            // }
        } catch (error) {
            console.error('Error resetting items:', error);
            message.error('Không thể reset danh sách');
        } finally {
            setLoading(false);
        }
    }, [fetchAllItems, getHomeItems]);

    const handleResetConfirm = useCallback(() => {
        Modal.confirm({
            title: 'Xác nhận làm mới danh sách',
            content: (
                <div>
                    <p>Bạn có chắc chắn muốn làm mới danh sách?</p>
                    <Alert
                        message="Lưu ý:"
                        description={
                            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                <li>Tất cả vị trí sắp xếp sẽ được đặt lại về mặc định</li>
                                <li>Trạng thái hiển thị của tất cả items sẽ được bật lại</li>
                                <li>Thao tác này không thể hoàn tác</li>
                            </ul>
                        }
                        type="warning"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                </div>
            ),
            okText: 'Xác nhận',
            cancelText: 'Hủy',
            onOk: handleResetItems,
            okButtonProps: { danger: true },
            width: 500
        });
    }, [handleResetItems]);

    const visibleItemsCount = useMemo(
        () => items.filter(item => item.isVisible).length,
        [items]
    );

    const storeOptions = useMemo(
        () => stores.map(store => ({
            label: store.name,
            value: store.id!
        })),
        [stores]
    );

    const sortableItemIds = useMemo(
        () => items.map(item => item.id),
        [items]
    );

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div className="admin-page-title">
                    <h1 ref={titleRef}>Quản lý trang chủ</h1>
                    <Tag color="blue">
                        {visibleItemsCount} sản phẩm
                    </Tag>
                    <Button 
                        type="primary" 
                        icon={<QuestionCircleOutlined />} 
                        onClick={() => setIsTourOpen(true)}
                        style={{ marginLeft: 8 }}
                    >
                        Hướng dẫn
                    </Button>
                </div>

                <div className="admin-page-actions">
                    <Space>
                        <Select
                            placeholder="Chọn cửa hàng"
                            style={{ width: 200 }}
                            value={selectedStoreId}
                            onChange={(value: string) => setSelectedStoreId(value)}
                            options={storeOptions}
                        />
                        <Button
                            onClick={handleResetConfirm}
                            type="primary"
                            danger
                            icon={<ReloadOutlined />}
                            className="admin-button"
                            ref={resetButtonRef}
                            disabled={!selectedStoreId}
                        >
                            Làm mới danh sách
                        </Button>
                    </Space>
                </div>
            </div>

            <div className="admin-form-section">
                <div ref={instructionsRef}>
                    <Alert
                        message="Hướng dẫn"
                        description={
                            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                <li><strong>Chọn cửa hàng:</strong> Trước tiên phải chọn cửa hàng để quản lý menu trang chủ</li>
                                <li><strong>Tuyển tập:</strong> Hiển thị chung cho tất cả cửa hàng</li>
                                <li><strong>Menu cửa hàng:</strong> Cà phê, đồ uống, món ăn dựa vào menu đã thiết lập của từng cửa hàng</li>
                                <li><strong>Kéo thả:</strong> Sắp xếp thứ tự hiển thị trên trang chủ</li>
                                <li><strong>Bật/tắt:</strong> Switch để hiển thị/ẩn item trên trang chủ</li>
                                <li>Thứ tự sắp xếp và trạng thái hiển thị sẽ được lưu tự động</li>
                            </ul>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                </div>

                <div className="admin-table-container" ref={tableContainerRef}>
                    {!selectedStoreId ? (
                        <Alert
                            message="Vui lòng chọn cửa hàng"
                            description="Bạn cần chọn một cửa hàng để quản lý menu trang chủ. Sử dụng dropdown ở góc trên bên phải để chọn cửa hàng."
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    ) : (
                        <DndContext
                            sensors={sensors}
                            onDragEnd={handleDragEnd}
                            collisionDetection={closestCenter}
                        >
                            <SortableContext
                                items={sortableItemIds}
                                strategy={verticalListSortingStrategy}
                            >
                                <Table
                                    className="admin-table"
                                    components={{
                                        body: {
                                            row: DraggableRow,
                                        },
                                    }}
                                    rowKey="id"
                                    columns={isMobile ? mobileColumns : columns}
                                    dataSource={items}
                                    loading={loading}
                                    pagination={false}
                                    bordered
                                    size={isMobile ? "small" : "middle"}
                                />
                            </SortableContext>
                        </DndContext>
                    )}
                </div>
            </div>

            {/* Tour component */}
            <ConfigProvider locale={viVN}>
                <Tour
                    open={isTourOpen}
                    onClose={() => setIsTourOpen(false)}
                    steps={tourSteps}
                    indicatorsRender={(current, total) => (
                        <span>{current + 1} / {total}</span>
                    )}
                    type="primary"
                    arrow={true}
                    placement="bottom"
                    mask={{
                        style: {
                            boxShadow: 'inset 0 0 15px #333',
                        },
                    }}
                />
            </ConfigProvider>
        </AdminLayout>
    );
};

export default HomeManagement;